import { DollarSign, Calendar, Users, Plus, RefreshCw } from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Button, PageHeader, SegmentedControl, Sparkline } from '../components/ui';
import { ManualSessionModal } from './sessions/ManualSessionModal';
import {
  KpiCard,
  TodaySessions,
  AttentionPanel,
  TeamPanel,
  GettingStarted,
  ConversionFunnel,
  UtilizationHeatmap,
  RevenueBreakdown,
  OutstandingPayments,
  PipelineRevenue,
  CancellationRate,
  UpcomingPreview,
  MethodMix,
  ClientRetention,
  TopClients,
  DormantClients,
  ProviderLeaderboard,
  FeeAccuracy,
} from '../components/dashboard';
import {
  GET_DASHBOARD_SUMMARY,
  GET_DASHBOARD_SESSIONS,
  GET_DASHBOARD_ATTENTION_ITEMS,
  GET_DASHBOARD_TEAM_CAPACITY,
  GET_DASHBOARD_CONVERSION_FUNNEL,
  GET_DASHBOARD_UTILIZATION_HEATMAP,
  GET_DASHBOARD_REVENUE_BREAKDOWN,
  GET_DASHBOARD_OUTSTANDING_PAYMENTS,
  GET_DASHBOARD_PIPELINE_REVENUE,
  GET_DASHBOARD_CANCELLATION_RATE,
  GET_DASHBOARD_UPCOMING_PREVIEW,
  GET_DASHBOARD_METHOD_MIX,
  GET_DASHBOARD_CLIENT_RETENTION,
  GET_DASHBOARD_TOP_CLIENTS,
  GET_DASHBOARD_DORMANT_CLIENTS,
  GET_DASHBOARD_PROVIDER_LEADERBOARD,
  GET_DASHBOARD_FEE_ACCURACY,
} from '../apollo/dashboard';
import type {
  DashboardSummaryData,
  DashboardSessionsData,
  DashboardAttentionData,
  DashboardTeamData,
  DashboardConversionFunnelData,
  DashboardUtilizationHeatmapData,
  DashboardRevenueBreakdownData,
  DashboardOutstandingPaymentsData,
  DashboardPipelineRevenueData,
  DashboardCancellationRateData,
  DashboardUpcomingPreviewData,
  DashboardMethodMixData,
  DashboardClientRetentionData,
  DashboardTopClientsData,
  DashboardDormantClientsData,
  DashboardProviderLeaderboardData,
  DashboardFeeAccuracyData,
} from '../types/dashboard';
import { useAuth } from '../auth/AuthProvider';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useDashboardPeriod } from '../hooks/useDashboardPeriod';
import type { DashboardPeriod } from '../hooks/useDashboardPeriod';
import { usePrimaryColor } from '../branding/usePrimaryColor';
import { toLocalDateStr, getBrowserTimezone, shiftDateStr } from '../utils/dateRange';
import { cumulativeSum } from '../utils/cumulative';
import { formatCurrency } from '../utils/currency';
import { isForbidden } from '../utils/isForbidden';
import { timeAgo, localDateKey } from '../utils/sessionTime';

// Map display labels to period values and vice versa
const PERIOD_DISPLAY_OPTIONS = ['Today', 'Week', 'Month', 'Quarter'] as const;
const DISPLAY_TO_PERIOD: Record<string, DashboardPeriod> = {
  Today: 'today', Week: 'week', Month: 'month', Quarter: 'quarter',
};
const PERIOD_TO_DISPLAY: Record<DashboardPeriod, string> = {
  today: 'Today', week: 'Week', month: 'Month', quarter: 'Quarter',
};

export function Dashboard() {
  const { user } = useAuth();
  const { period, setPeriod, label: periodLabel, sessionDateRange, kpiPeriod } = useDashboardPeriod();
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const date = toLocalDateStr(new Date());
  const timezone = getBrowserTimezone();
  const { startDate, endDate } = sessionDateRange;

  const activeDisplay = PERIOD_TO_DISPLAY[period];

  const summary = useQuery<DashboardSummaryData>(GET_DASHBOARD_SUMMARY, {
    variables: { date, timezone, revenuePeriod: kpiPeriod, sessionsPeriod: kpiPeriod },
  });
  // The backend filters sessions by their stored (naive-UTC) date, but we
  // group and display them in the viewer's local timezone — a session's local
  // date can differ from its stored date by up to a day. Over-fetch one day on
  // each side so no in-window session is missed, then filter to the true local
  // window below.
  const sessions = useQuery<DashboardSessionsData>(GET_DASHBOARD_SESSIONS, {
    variables: { startDate: shiftDateStr(startDate, -1), endDate: shiftDateStr(endDate, 1) },
  });
  const attention = useQuery<DashboardAttentionData>(GET_DASHBOARD_ATTENTION_ITEMS, {
    variables: { limit: 10 },
  });
  const team = useQuery<DashboardTeamData>(GET_DASHBOARD_TEAM_CAPACITY, {
    variables: { date },
  });
  const conversionFunnel = useQuery<DashboardConversionFunnelData>(GET_DASHBOARD_CONVERSION_FUNNEL, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const utilizationHeatmap = useQuery<DashboardUtilizationHeatmapData>(GET_DASHBOARD_UTILIZATION_HEATMAP, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const revenueBreakdown = useQuery<DashboardRevenueBreakdownData>(GET_DASHBOARD_REVENUE_BREAKDOWN, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const outstandingPayments = useQuery<DashboardOutstandingPaymentsData>(GET_DASHBOARD_OUTSTANDING_PAYMENTS);
  const pipelineRevenue = useQuery<DashboardPipelineRevenueData>(GET_DASHBOARD_PIPELINE_REVENUE, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const cancellationRate = useQuery<DashboardCancellationRateData>(GET_DASHBOARD_CANCELLATION_RATE, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const upcomingPreview = useQuery<DashboardUpcomingPreviewData>(GET_DASHBOARD_UPCOMING_PREVIEW, {
    variables: { timezone },
  });
  const methodMix = useQuery<DashboardMethodMixData>(GET_DASHBOARD_METHOD_MIX, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const clientRetention = useQuery<DashboardClientRetentionData>(GET_DASHBOARD_CLIENT_RETENTION, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const topClients = useQuery<DashboardTopClientsData>(GET_DASHBOARD_TOP_CLIENTS);
  const dormantClients = useQuery<DashboardDormantClientsData>(GET_DASHBOARD_DORMANT_CLIENTS);
  const providerLeaderboard = useQuery<DashboardProviderLeaderboardData>(GET_DASHBOARD_PROVIDER_LEADERBOARD, {
    variables: { date, timezone, period: kpiPeriod },
  });
  const feeAccuracy = useQuery<DashboardFeeAccuracyData>(GET_DASHBOARD_FEE_ACCURACY, {
    variables: { date, timezone, period: kpiPeriod },
  });

  // Keep only sessions whose browser-local date falls in the selected window
  // (the query over-fetched by a day on each side; see above).
  const windowedSessions = useMemo(() => {
    const all = sessions.data?.dashboardSessions ?? [];
    return all.filter((s) => {
      const key = localDateKey(s.date, s.startTime);
      return key >= startDate && key <= endDate;
    });
  }, [sessions.data, startDate, endDate]);

  const isRefreshing = summary.loading || sessions.loading || attention.loading || team.loading || conversionFunnel.loading || utilizationHeatmap.loading || revenueBreakdown.loading || outstandingPayments.loading || pipelineRevenue.loading || cancellationRate.loading || upcomingPreview.loading || methodMix.loading || clientRetention.loading || topClients.loading || dormantClients.loading || providerLeaderboard.loading || feeAccuracy.loading;
  const isLg = useMediaQuery('(min-width: 1024px)');
  const primaryColor = usePrimaryColor();

  const handleRefresh = () => {
    summary.refetch();
    sessions.refetch();
    attention.refetch();
    team.refetch();
    conversionFunnel.refetch();
    utilizationHeatmap.refetch();
    revenueBreakdown.refetch();
    outstandingPayments.refetch();
    pipelineRevenue.refetch();
    cancellationRate.refetch();
    upcomingPreview.refetch();
    methodMix.refetch();
    clientRetention.refetch();
    topClients.refetch();
    dormantClients.refetch();
    providerLeaderboard.refetch();
    feeAccuracy.refetch();
    setLastRefresh(new Date());
  };

  // Refresh data when user returns to tab after 60+ seconds away
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const hiddenAt = hiddenAtRef.current;
        if (hiddenAt && Date.now() - hiddenAt > 60_000) {
          handleRefresh();
        }
        hiddenAtRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []); // handleRefresh is stable enough (uses query refetch methods)

  // New-tenant detection: no bookings, no capacity, no clients, no revenue ever
  const summaryData = summary.data?.dashboardSummary;
  const isNewTenant =
    !!summaryData &&
    summaryData.sessions.booked === 0 &&
    summaryData.sessions.capacity === 0 &&
    summaryData.activeClients.total === 0 &&
    summaryData.revenue.hasAnyRevenue === false;

  // Greeting logic
  const hour = new Date().getHours();
  const greetingPrefix = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0];
  const greeting = firstName ? `${greetingPrefix}, ${firstName}.` : `${greetingPrefix}.`;

  // Helper to determine panel state with precedence: forbidden > error > loading > empty > data
  function panelState(query: { loading: boolean; error?: any; data?: any }) {
    if (isForbidden(query.error)) return 'forbidden';
    if (query.error) return 'error';
    if (query.loading && !query.data) return 'loading';
    if (!query.data) return 'empty';
    return 'data';
  }

  // Format weekday label for sparkline bars
  const getWeekdayLabel = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[d.getDay()];
  };

  // Check if today's date matches for bar emphasis
  const isToday = (dateStr: string): boolean => {
    const today = new Date();
    const todayStr = toLocalDateStr(today);
    return dateStr === todayStr;
  };

  return (
    <div>
      <PageHeader
        title={greeting}
        controls={
          <>
            <SegmentedControl
              options={[...PERIOD_DISPLAY_OPTIONS]}
              value={activeDisplay}
              onChange={(v: string) => setPeriod(DISPLAY_TO_PERIOD[v])}
            />
            <Button
              variant="secondary"
              icon={
                isRefreshing ? (
                  <RefreshCw size={13} className="animate-spin" />
                ) : (
                  <RefreshCw size={13} />
                )
              }
              onClick={handleRefresh}
            >
              {lastRefresh ? `Updated ${timeAgo(lastRefresh.toISOString())}` : "Refresh"}
            </Button>
          </>
        }
        actions={
          <Button
            variant="primary"
            icon={<Plus size={13} strokeWidth={2.2} />}
            onClick={() => setCreateOpen(true)}
          >
            New session
          </Button>
        }
        mobileAction={{
          label: "New session",
          icon: <Plus size={16} strokeWidth={2.2} />,
          onClick: () => setCreateOpen(true),
        }}
      />

      <div className="px-4 md:px-7 pb-6">
        {/* ── KPI grid ────────────────────────────────────────────── */}
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {/* Revenue today */}
          {panelState(summary) === 'forbidden' ? (
            <div className="bg-paper-hi rounded-lg p-5 shadow-card flex items-center justify-center">
              <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
            </div>
          ) : panelState(summary) === 'error' ? (
            <div className="bg-paper-hi rounded-lg p-5 shadow-card">
              <p className="text-[13px] text-ink-4 mb-3">Couldn't load revenue.</p>
              <button
                onClick={() => summary.refetch()}
                className="text-[11.5px] font-medium text-tide hover:text-tide/80"
              >
                Retry
              </button>
            </div>
          ) : (
            <KpiCard
              label={`Revenue · ${periodLabel.toLowerCase()}`}
              loading={panelState(summary) === 'loading'}
              tooltip="Confirmed payments with positive amount"
              value={
                summary.data?.dashboardSummary.revenue.total !== undefined
                  ? `$${formatCurrency(summary.data.dashboardSummary.revenue.total)}`
                  : '$0.00'
              }
              delta={
                summary.data?.dashboardSummary.revenue &&
                summary.data.dashboardSummary.revenue.hasAnyRevenue &&
                summary.data.dashboardSummary.revenue.deltaPct !== null &&
                summary.data.dashboardSummary.revenue.total &&
                summary.data.dashboardSummary.revenue.total > 0
                  ? {
                      direction: summary.data.dashboardSummary.revenue.deltaPct > 0 ? 'up' : 'down',
                      value: `${Math.abs(summary.data.dashboardSummary.revenue.deltaPct).toFixed(1)}%`,
                      context: `vs prev ${kpiPeriod}`,
                    }
                  : undefined
              }
              icon={
                <DollarSign size={11} stroke={primaryColor} strokeWidth={2.2} />
              }
              accentBg="bg-tide-ss"
              children={
                !summary.data?.dashboardSummary.revenue.hasAnyRevenue ? (
                  <p className="text-[11.5px] text-ink-4">
                    Revenue will appear after your first payment is confirmed.
                  </p>
                ) : summary.data.dashboardSummary.revenue.sparkline &&
                  summary.data.dashboardSummary.revenue.sparkline.length > 0 ? (
                  <Sparkline data={cumulativeSum(summary.data.dashboardSummary.revenue.sparkline)} />
                ) : undefined
              }
            />
          )}

          {/* Sessions this week */}
          {panelState(summary) === 'forbidden' ? (
            <div className="bg-paper-hi rounded-lg p-5 shadow-card flex items-center justify-center">
              <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
            </div>
          ) : panelState(summary) === 'error' ? (
            <div className="bg-paper-hi rounded-lg p-5 shadow-card">
              <p className="text-[13px] text-ink-4 mb-3">Couldn't load sessions.</p>
              <button
                onClick={() => summary.refetch()}
                className="text-[11.5px] font-medium text-tide hover:text-tide/80"
              >
                Retry
              </button>
            </div>
          ) : (
            <KpiCard
              label={`Sessions · ${periodLabel.toLowerCase()}`}
              loading={panelState(summary) === 'loading'}
              tooltip="Live bookings (excludes cancelled and deleted)"
              value={
                summary.data?.dashboardSummary.sessions.booked !== undefined
                  ? summary.data.dashboardSummary.sessions.booked.toString()
                  : '0'
              }
              secondaryValue={
                summary.data?.dashboardSummary.sessions.capacity !== undefined
                  ? `${summary.data.dashboardSummary.sessions.capacity} capacity`
                  : undefined
              }
              delta={
                summary.data?.dashboardSummary.sessions.deltaVsPrevious
                  ? {
                      direction:
                        summary.data.dashboardSummary.sessions.deltaVsPrevious > 0 ? 'up' : 'down',
                      value: `${Math.abs(summary.data.dashboardSummary.sessions.deltaVsPrevious)}`,
                      context: `vs prev ${kpiPeriod}`,
                    }
                  : undefined
              }
              icon={
                <Calendar size={11} stroke={primaryColor} strokeWidth={2.2} />
              }
              accentBg="bg-tide-ss"
              children={
                kpiPeriod === 'week' &&
                summary.data?.dashboardSummary.sessions.byDay &&
                summary.data.dashboardSummary.sessions.byDay.length > 0 ? (
                  <>
                    <div className="grid grid-cols-7 gap-1">
                      {summary.data.dashboardSummary.sessions.byDay.map((day) => (
                        <div
                          key={day.date}
                          className={`h-6 rounded-sm ${
                            isToday(day.date) ? 'bg-tide' : 'bg-ink-6'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="mt-1.5 flex justify-between text-[10px] text-ink-4">
                      {summary.data.dashboardSummary.sessions.byDay.map((day) => (
                        <span
                          key={day.date}
                          className={isToday(day.date) ? 'text-tide font-semibold' : ''}
                        >
                          {getWeekdayLabel(day.date)}
                        </span>
                      ))}
                    </div>
                  </>
                ) : undefined
              }
            />
          )}

          {/* Active clients */}
          {panelState(summary) === 'forbidden' ? (
            <div className="bg-paper-hi rounded-lg p-5 shadow-card flex items-center justify-center">
              <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
            </div>
          ) : panelState(summary) === 'error' ? (
            <div className="bg-paper-hi rounded-lg p-5 shadow-card">
              <p className="text-[13px] text-ink-4 mb-3">Couldn't load active clients.</p>
              <button
                onClick={() => summary.refetch()}
                className="text-[11.5px] font-medium text-tide hover:text-tide/80"
              >
                Retry
              </button>
            </div>
          ) : (
            <KpiCard
              label="Active clients"
              loading={panelState(summary) === 'loading'}
              tooltip="Clients with at least one live booking"
              value={
                summary.data?.dashboardSummary.activeClients.total !== undefined
                  ? summary.data.dashboardSummary.activeClients.total.toString()
                  : '0'
              }
              delta={
                summary.data?.dashboardSummary.activeClients.newInWindow !== undefined &&
                summary.data.dashboardSummary.activeClients.newInWindow > 0
                  ? {
                      direction: 'up',
                      value: `+${summary.data.dashboardSummary.activeClients.newInWindow} new`,
                      context: 'past 30 days',
                    }
                  : undefined
              }
              icon={
                <Users size={11} stroke={primaryColor} strokeWidth={2.2} />
              }
              accentBg="bg-tide-ss"
              children={
                summary.data?.dashboardSummary.activeClients.total ? (
                  <div className="space-y-1.5">
                    {summary.data.dashboardSummary.activeClients.returning > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <div
                            className="h-1.5 rounded-full overflow-hidden"
                            style={{
                              backgroundColor: 'rgba(230, 230, 230, 0.3)',
                            }}
                          >
                            <div
                              className="h-full rounded-full bg-ink-2"
                              style={{
                                width: `${
                                  (summary.data.dashboardSummary.activeClients.returning /
                                    summary.data.dashboardSummary.activeClients.total) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                        <span className="text-[10.5px] text-ink-4 w-20 flex items-center justify-between">
                          <span>Returning</span>
                          <span className="num">
                            {summary.data.dashboardSummary.activeClients.returning}
                          </span>
                        </span>
                      </div>
                    )}
                    {summary.data.dashboardSummary.activeClients.firstTime > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <div
                            className="h-1.5 rounded-full overflow-hidden"
                            style={{
                              backgroundColor: 'rgba(230, 230, 230, 0.3)',
                            }}
                          >
                            <div
                              className="h-full rounded-full bg-tide"
                              style={{
                                width: `${
                                  (summary.data.dashboardSummary.activeClients.firstTime /
                                    summary.data.dashboardSummary.activeClients.total) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                        <span className="text-[10.5px] text-ink-4 w-20 flex items-center justify-between">
                          <span>New</span>
                          <span className="num">
                            {summary.data.dashboardSummary.activeClients.firstTime}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                ) : undefined
              }
            />
          )}

        </div>
      </div>

      {/* ── Main grid ───────────────────────────────────────────────── */}
      <div className="px-4 md:px-7 pb-14">
        {isNewTenant ? (
          <GettingStarted />
        ) : isLg ? (
          <>
          <div className="grid grid-cols-12 gap-3">
            {/* Today's agenda */}
            <div className="col-span-8">
              {panelState(sessions) === 'forbidden' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
                </div>
              ) : panelState(sessions) === 'error' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-[13px] text-ink-4 mb-3">Couldn't load sessions.</p>
                  <button
                    onClick={() => sessions.refetch()}
                    className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <TodaySessions
                  sessions={windowedSessions}
                  periodLabel={periodLabel}
                  loading={panelState(sessions) === 'loading'}
                  empty={windowedSessions.length === 0}
                />
              )}
            </div>

            {/* Right column */}
            <div className="col-span-4 space-y-3">
              {/* Attention panel */}
              {panelState(attention) === 'forbidden' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
                </div>
              ) : panelState(attention) === 'error' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-[13px] text-ink-4 mb-3">Couldn't load attention items.</p>
                  <button
                    onClick={() => attention.refetch()}
                    className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <AttentionPanel
                  items={attention.data?.dashboardAttentionItems ?? []}
                  loading={attention.loading}
                />
              )}

              {/* Team panel */}
              {panelState(team) === 'forbidden' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
                </div>
              ) : panelState(team) === 'error' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-[13px] text-ink-4 mb-3">Couldn't load team capacity.</p>
                  <button
                    onClick={() => team.refetch()}
                    className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <TeamPanel
                  members={team.data?.dashboardTeamCapacity ?? []}
                  loading={team.loading}
                />
              )}
            </div>
          </div>

          {/* Secondary panels — two flowing columns (masonry) */}
          <div className="columns-2 gap-3 mt-3 [&>*]:mb-3 [&>*]:break-inside-avoid">
            <div>
              {panelState(conversionFunnel) === 'forbidden' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
                </div>
              ) : panelState(conversionFunnel) === 'error' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-[13px] text-ink-4 mb-3">Couldn't load conversion funnel.</p>
                  <button
                    onClick={() => conversionFunnel.refetch()}
                    className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <ConversionFunnel
                  leads={conversionFunnel.data?.dashboardConversionFunnel.leads ?? 0}
                  booked={conversionFunnel.data?.dashboardConversionFunnel.booked ?? 0}
                  completed={conversionFunnel.data?.dashboardConversionFunnel.completed ?? 0}
                  paid={conversionFunnel.data?.dashboardConversionFunnel.paid ?? 0}
                  medianTimeToBookHours={conversionFunnel.data?.dashboardConversionFunnel.medianTimeToBookHours ?? null}
                  loading={conversionFunnel.loading}
                  periodLabel={periodLabel}
                />
              )}
            </div>
            <div>
              {panelState(utilizationHeatmap) === 'forbidden' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
                </div>
              ) : panelState(utilizationHeatmap) === 'error' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-[13px] text-ink-4 mb-3">Couldn't load capacity heatmap.</p>
                  <button
                    onClick={() => utilizationHeatmap.refetch()}
                    className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <UtilizationHeatmap
                  cells={utilizationHeatmap.data?.dashboardUtilizationHeatmap.cells ?? []}
                  loading={utilizationHeatmap.loading}
                  periodLabel={periodLabel}
                />
              )}
            </div>

            <div>
              {panelState(revenueBreakdown) === 'forbidden' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
                </div>
              ) : panelState(revenueBreakdown) === 'error' ? (
                <div className="bg-paper-hi rounded-lg shadow-card p-5">
                  <p className="text-[13px] text-ink-4 mb-3">Couldn't load revenue breakdown.</p>
                  <button
                    onClick={() => revenueBreakdown.refetch()}
                    className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <RevenueBreakdown
                  byService={revenueBreakdown.data?.dashboardRevenueBreakdown.byService ?? []}
                  byProvider={revenueBreakdown.data?.dashboardRevenueBreakdown.byProvider ?? []}
                  loading={revenueBreakdown.loading}
                  periodLabel={periodLabel}
                />
              )}
            </div>
            <div>
              <OutstandingPayments
                totalAmount={outstandingPayments.data?.dashboardOutstandingPayments.totalAmount ?? 0}
                count={outstandingPayments.data?.dashboardOutstandingPayments.count ?? 0}
                byState={outstandingPayments.data?.dashboardOutstandingPayments.byState ?? []}
                loading={outstandingPayments.loading}
                error={panelState(outstandingPayments) === 'error'}
                forbidden={panelState(outstandingPayments) === 'forbidden'}
                onRetry={() => outstandingPayments.refetch()}
              />
            </div>

            <div>
              <PipelineRevenue
                totalProjected={pipelineRevenue.data?.dashboardPipelineRevenue.totalProjected ?? 0}
                bookingCount={pipelineRevenue.data?.dashboardPipelineRevenue.bookingCount ?? 0}
                byDay={pipelineRevenue.data?.dashboardPipelineRevenue.byDay ?? []}
                loading={pipelineRevenue.loading}
                error={panelState(pipelineRevenue) === 'error'}
                forbidden={panelState(pipelineRevenue) === 'forbidden'}
                periodLabel={periodLabel}
                onRetry={() => pipelineRevenue.refetch()}
              />
            </div>
            <div>
              <CancellationRate
                cancelledCount={cancellationRate.data?.dashboardCancellationRate.cancelledCount ?? 0}
                totalCount={cancellationRate.data?.dashboardCancellationRate.totalCount ?? 0}
                rate={cancellationRate.data?.dashboardCancellationRate.rate ?? null}
                previousRate={cancellationRate.data?.dashboardCancellationRate.previousRate ?? null}
                loading={cancellationRate.loading}
                error={panelState(cancellationRate) === 'error'}
                forbidden={panelState(cancellationRate) === 'forbidden'}
                periodLabel={periodLabel}
                onRetry={() => cancellationRate.refetch()}
              />
            </div>

            <div>
              <UpcomingPreview
                days={upcomingPreview.data?.dashboardUpcomingPreview.days ?? []}
                totalCount={upcomingPreview.data?.dashboardUpcomingPreview.totalCount ?? 0}
                loading={upcomingPreview.loading}
                error={panelState(upcomingPreview) === 'error'}
                forbidden={panelState(upcomingPreview) === 'forbidden'}
                onRetry={() => upcomingPreview.refetch()}
              />
            </div>
            <div>
              <MethodMix
                totals={methodMix.data?.dashboardMethodMix.totals ?? []}
                total={methodMix.data?.dashboardMethodMix.total ?? 0}
                loading={methodMix.loading}
                error={panelState(methodMix) === 'error'}
                forbidden={panelState(methodMix) === 'forbidden'}
                periodLabel={periodLabel}
                onRetry={() => methodMix.refetch()}
              />
            </div>

            <div>
              <ClientRetention
                newCount={clientRetention.data?.dashboardClientRetention.newCount ?? 0}
                returningCount={clientRetention.data?.dashboardClientRetention.returningCount ?? 0}
                buckets={clientRetention.data?.dashboardClientRetention.buckets ?? []}
                loading={clientRetention.loading}
                error={panelState(clientRetention) === 'error'}
                forbidden={panelState(clientRetention) === 'forbidden'}
                periodLabel={periodLabel}
                onRetry={() => clientRetention.refetch()}
              />
            </div>
            <div>
              <TopClients
                clients={topClients.data?.dashboardTopClients.clients ?? []}
                loading={topClients.loading}
                error={panelState(topClients) === 'error'}
                forbidden={panelState(topClients) === 'forbidden'}
                onRetry={() => topClients.refetch()}
              />
            </div>

            <div>
              <ProviderLeaderboard
                providers={providerLeaderboard.data?.dashboardProviderLeaderboard.providers ?? []}
                loading={providerLeaderboard.loading}
                error={panelState(providerLeaderboard) === 'error'}
                forbidden={panelState(providerLeaderboard) === 'forbidden'}
                periodLabel={periodLabel}
                onRetry={() => providerLeaderboard.refetch()}
              />
            </div>
            <div>
              <DormantClients
                clients={dormantClients.data?.dashboardDormantClients.clients ?? []}
                count={dormantClients.data?.dashboardDormantClients.count ?? 0}
                loading={dormantClients.loading}
                error={panelState(dormantClients) === 'error'}
                forbidden={panelState(dormantClients) === 'forbidden'}
                onRetry={() => dormantClients.refetch()}
              />
            </div>

            <div>
              <FeeAccuracy
                totalEstimated={feeAccuracy.data?.dashboardFeeAccuracy.totalEstimated ?? 0}
                totalActual={feeAccuracy.data?.dashboardFeeAccuracy.totalActual ?? 0}
                delta={feeAccuracy.data?.dashboardFeeAccuracy.delta ?? 0}
                deltaPct={feeAccuracy.data?.dashboardFeeAccuracy.deltaPct ?? null}
                count={feeAccuracy.data?.dashboardFeeAccuracy.count ?? 0}
                overCount={feeAccuracy.data?.dashboardFeeAccuracy.overCount ?? 0}
                underCount={feeAccuracy.data?.dashboardFeeAccuracy.underCount ?? 0}
                matchCount={feeAccuracy.data?.dashboardFeeAccuracy.matchCount ?? 0}
                loading={feeAccuracy.loading}
                error={panelState(feeAccuracy) === 'error'}
                forbidden={panelState(feeAccuracy) === 'forbidden'}
                periodLabel={periodLabel}
                onRetry={() => feeAccuracy.refetch()}
              />
            </div>
          </div>
          </>
        ) : (
          <div className="space-y-3">
            {/* Today's agenda (mobile) */}
            {panelState(sessions) === 'forbidden' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
              </div>
            ) : panelState(sessions) === 'error' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-[13px] text-ink-4 mb-3">Couldn't load sessions.</p>
                <button
                  onClick={() => sessions.refetch()}
                  className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                >
                  Retry
                </button>
              </div>
            ) : (
              <TodaySessions
                sessions={windowedSessions}
                periodLabel={periodLabel}
                loading={panelState(sessions) === 'loading'}
                empty={windowedSessions.length === 0}
              />
            )}

            {/* Attention panel (mobile) */}
            {panelState(attention) === 'forbidden' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
              </div>
            ) : panelState(attention) === 'error' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-[13px] text-ink-4 mb-3">Couldn't load attention items.</p>
                <button
                  onClick={() => attention.refetch()}
                  className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                >
                  Retry
                </button>
              </div>
            ) : (
              <AttentionPanel
                items={attention.data?.dashboardAttentionItems ?? []}
                loading={attention.loading}
              />
            )}

            {/* Team panel (mobile) */}
            {panelState(team) === 'forbidden' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
              </div>
            ) : panelState(team) === 'error' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-[13px] text-ink-4 mb-3">Couldn't load team capacity.</p>
                <button
                  onClick={() => team.refetch()}
                  className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                >
                  Retry
                </button>
              </div>
            ) : (
              <TeamPanel
                members={team.data?.dashboardTeamCapacity ?? []}
                loading={team.loading}
              />
            )}

            {/* Conversion funnel (mobile) */}
            {panelState(conversionFunnel) === 'forbidden' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
              </div>
            ) : panelState(conversionFunnel) === 'error' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-[13px] text-ink-4 mb-3">Couldn't load conversion funnel.</p>
                <button
                  onClick={() => conversionFunnel.refetch()}
                  className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                >
                  Retry
                </button>
              </div>
            ) : (
              <ConversionFunnel
                leads={conversionFunnel.data?.dashboardConversionFunnel.leads ?? 0}
                booked={conversionFunnel.data?.dashboardConversionFunnel.booked ?? 0}
                completed={conversionFunnel.data?.dashboardConversionFunnel.completed ?? 0}
                paid={conversionFunnel.data?.dashboardConversionFunnel.paid ?? 0}
                medianTimeToBookHours={conversionFunnel.data?.dashboardConversionFunnel.medianTimeToBookHours ?? null}
                loading={conversionFunnel.loading}
                periodLabel={periodLabel}
              />
            )}

            {/* Utilization heatmap (mobile) */}
            {panelState(utilizationHeatmap) === 'forbidden' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
              </div>
            ) : panelState(utilizationHeatmap) === 'error' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-[13px] text-ink-4 mb-3">Couldn't load capacity heatmap.</p>
                <button
                  onClick={() => utilizationHeatmap.refetch()}
                  className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                >
                  Retry
                </button>
              </div>
            ) : (
              <UtilizationHeatmap
                cells={utilizationHeatmap.data?.dashboardUtilizationHeatmap.cells ?? []}
                loading={utilizationHeatmap.loading}
                periodLabel={periodLabel}
              />
            )}

            {/* Revenue breakdown (mobile) */}
            {panelState(revenueBreakdown) === 'forbidden' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-ink-4 text-[13px] text-center">You don't have access to this.</p>
              </div>
            ) : panelState(revenueBreakdown) === 'error' ? (
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <p className="text-[13px] text-ink-4 mb-3">Couldn't load revenue breakdown.</p>
                <button
                  onClick={() => revenueBreakdown.refetch()}
                  className="text-[11.5px] font-medium text-tide hover:text-tide/80"
                >
                  Retry
                </button>
              </div>
            ) : (
              <RevenueBreakdown
                byService={revenueBreakdown.data?.dashboardRevenueBreakdown.byService ?? []}
                byProvider={revenueBreakdown.data?.dashboardRevenueBreakdown.byProvider ?? []}
                loading={revenueBreakdown.loading}
                periodLabel={periodLabel}
              />
            )}

            {/* Outstanding payments (mobile) */}
            <OutstandingPayments
              totalAmount={outstandingPayments.data?.dashboardOutstandingPayments.totalAmount ?? 0}
              count={outstandingPayments.data?.dashboardOutstandingPayments.count ?? 0}
              byState={outstandingPayments.data?.dashboardOutstandingPayments.byState ?? []}
              loading={outstandingPayments.loading}
              error={panelState(outstandingPayments) === 'error'}
              forbidden={panelState(outstandingPayments) === 'forbidden'}
              onRetry={() => outstandingPayments.refetch()}
            />

            {/* Pipeline revenue (mobile) */}
            <PipelineRevenue
              totalProjected={pipelineRevenue.data?.dashboardPipelineRevenue.totalProjected ?? 0}
              bookingCount={pipelineRevenue.data?.dashboardPipelineRevenue.bookingCount ?? 0}
              byDay={pipelineRevenue.data?.dashboardPipelineRevenue.byDay ?? []}
              loading={pipelineRevenue.loading}
              error={panelState(pipelineRevenue) === 'error'}
              forbidden={panelState(pipelineRevenue) === 'forbidden'}
              periodLabel={periodLabel}
              onRetry={() => pipelineRevenue.refetch()}
            />

            {/* Cancellation rate (mobile) */}
            <CancellationRate
              cancelledCount={cancellationRate.data?.dashboardCancellationRate.cancelledCount ?? 0}
              totalCount={cancellationRate.data?.dashboardCancellationRate.totalCount ?? 0}
              rate={cancellationRate.data?.dashboardCancellationRate.rate ?? null}
              previousRate={cancellationRate.data?.dashboardCancellationRate.previousRate ?? null}
              loading={cancellationRate.loading}
              error={panelState(cancellationRate) === 'error'}
              forbidden={panelState(cancellationRate) === 'forbidden'}
              periodLabel={periodLabel}
              onRetry={() => cancellationRate.refetch()}
            />

            {/* Week ahead (mobile) */}
            <UpcomingPreview
              days={upcomingPreview.data?.dashboardUpcomingPreview.days ?? []}
              totalCount={upcomingPreview.data?.dashboardUpcomingPreview.totalCount ?? 0}
              loading={upcomingPreview.loading}
              error={panelState(upcomingPreview) === 'error'}
              forbidden={panelState(upcomingPreview) === 'forbidden'}
              onRetry={() => upcomingPreview.refetch()}
            />

            {/* Booking methods (mobile) */}
            <MethodMix
              totals={methodMix.data?.dashboardMethodMix.totals ?? []}
              total={methodMix.data?.dashboardMethodMix.total ?? 0}
              loading={methodMix.loading}
              error={panelState(methodMix) === 'error'}
              forbidden={panelState(methodMix) === 'forbidden'}
              periodLabel={periodLabel}
              onRetry={() => methodMix.refetch()}
            />

            {/* Client retention (mobile) */}
            <ClientRetention
              newCount={clientRetention.data?.dashboardClientRetention.newCount ?? 0}
              returningCount={clientRetention.data?.dashboardClientRetention.returningCount ?? 0}
              buckets={clientRetention.data?.dashboardClientRetention.buckets ?? []}
              loading={clientRetention.loading}
              error={panelState(clientRetention) === 'error'}
              forbidden={panelState(clientRetention) === 'forbidden'}
              periodLabel={periodLabel}
              onRetry={() => clientRetention.refetch()}
            />

            {/* Top clients (mobile) */}
            <TopClients
              clients={topClients.data?.dashboardTopClients.clients ?? []}
              loading={topClients.loading}
              error={panelState(topClients) === 'error'}
              forbidden={panelState(topClients) === 'forbidden'}
              onRetry={() => topClients.refetch()}
            />

            {/* Provider leaderboard (mobile) */}
            <ProviderLeaderboard
              providers={providerLeaderboard.data?.dashboardProviderLeaderboard.providers ?? []}
              loading={providerLeaderboard.loading}
              error={panelState(providerLeaderboard) === 'error'}
              forbidden={panelState(providerLeaderboard) === 'forbidden'}
              periodLabel={periodLabel}
              onRetry={() => providerLeaderboard.refetch()}
            />

            {/* At-risk clients (mobile) */}
            <DormantClients
              clients={dormantClients.data?.dashboardDormantClients.clients ?? []}
              count={dormantClients.data?.dashboardDormantClients.count ?? 0}
              loading={dormantClients.loading}
              error={panelState(dormantClients) === 'error'}
              forbidden={panelState(dormantClients) === 'forbidden'}
              onRetry={() => dormantClients.refetch()}
            />

            {/* Fee accuracy (mobile) */}
            <FeeAccuracy
              totalEstimated={feeAccuracy.data?.dashboardFeeAccuracy.totalEstimated ?? 0}
              totalActual={feeAccuracy.data?.dashboardFeeAccuracy.totalActual ?? 0}
              delta={feeAccuracy.data?.dashboardFeeAccuracy.delta ?? 0}
              deltaPct={feeAccuracy.data?.dashboardFeeAccuracy.deltaPct ?? null}
              count={feeAccuracy.data?.dashboardFeeAccuracy.count ?? 0}
              overCount={feeAccuracy.data?.dashboardFeeAccuracy.overCount ?? 0}
              underCount={feeAccuracy.data?.dashboardFeeAccuracy.underCount ?? 0}
              matchCount={feeAccuracy.data?.dashboardFeeAccuracy.matchCount ?? 0}
              loading={feeAccuracy.loading}
              error={panelState(feeAccuracy) === 'error'}
              forbidden={panelState(feeAccuracy) === 'forbidden'}
              periodLabel={periodLabel}
              onRetry={() => feeAccuracy.refetch()}
            />
          </div>
        )}
      </div>

      <ManualSessionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleRefresh}
      />
    </div>
  );
}
